import React, { FC } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageToggle } from '../ui/LanguageToggle';

export interface NavItem {
  id: string;
  label: string;
  icon: string;
  path: string;
  badge?: number;
}

interface SidebarProps {
  mainMenuItems: NavItem[];
  settingsMenuItems: NavItem[];
  currentUser: {
    name: string;
    avatar?: string;
  };
  onLogout?: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  /**
   * Fires when a nav item with a hash path (e.g. `#senior-phone`) is clicked.
   * Used for the dynamic "[Senior]'s Phone" entry that opens a drawer instead
   * of navigating to a route.
   */
  onItemClick?: (itemId: string) => void;
}

interface NavLinkProps {
  item: NavItem;
  isActive: boolean;
  collapsed: boolean;
  onClick: (e: React.MouseEvent<HTMLAnchorElement>, item: NavItem) => void;
}

const NavLink: FC<NavLinkProps> = ({ item, isActive, collapsed, onClick }) => (
  <Link
    to={item.path}
    onClick={(e) => onClick(e, item)}
    title={collapsed ? item.label : undefined}
    aria-current={isActive ? 'page' : undefined}
    className={`relative flex items-center rounded-xl transition-all duration-200 group ${
      collapsed ? 'justify-center p-2.5' : 'gap-3 px-3 py-2.5'
    } ${
      isActive
        ? 'bg-brand-primary/[0.08] text-brand-primary'
        : 'text-brand-text-light hover:bg-brand-bg-warm hover:text-brand-text hover:translate-x-[1px]'
    }`}
  >
    {/* Left rail accent — visible on active, faint on hover */}
    <span
      aria-hidden
      className={`absolute left-0 top-1/2 -translate-y-1/2 w-[3px] rounded-r-full bg-brand-primary transition-all duration-200 ${
        isActive ? 'h-6 opacity-100' : 'h-4 opacity-0 group-hover:opacity-30'
      }`}
    />
    <span
      className={`material-symbols-outlined text-[22px] transition-all ${
        isActive ? 'text-brand-primary' : 'opacity-60 group-hover:opacity-100'
      }`}
      style={isActive ? { fontVariationSettings: "'FILL' 1" } : {}}
    >
      {item.icon}
    </span>
    {!collapsed && (
      <span className={`text-[15px] truncate ${isActive ? 'font-semibold' : 'font-medium'}`}>
        {item.label}
      </span>
    )}
    {item.badge && item.badge > 0 && (
      <span
        className={`flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-brand-accent text-white text-[11px] font-bold ${
          collapsed ? 'absolute -top-1 -right-1' : 'ml-auto'
        }`}
      >
        {item.badge > 99 ? '99+' : item.badge}
      </span>
    )}
    {collapsed && (
      <span className="absolute left-full ml-3 px-2.5 py-1.5 bg-brand-text text-white text-xs font-medium rounded-md opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 shadow-lg">
        {item.label}
      </span>
    )}
  </Link>
);

export const Sidebar: FC<SidebarProps> = ({
  mainMenuItems,
  settingsMenuItems,
  currentUser,
  onLogout,
  collapsed = false,
  onToggleCollapse,
  onItemClick,
}) => {
  const location = useLocation();
  const { t } = useTranslation();

  const handleItemClick = (e: React.MouseEvent<HTMLAnchorElement>, item: NavItem) => {
    if (item.path.startsWith('#')) {
      e.preventDefault();
      onItemClick?.(item.id);
    }
  };

  // Pick the caregiver's first initial for the avatar fallback
  const firstInitial = (currentUser.name || 'C').trim().charAt(0).toUpperCase();

  return (
    <aside
      className={`relative flex-col h-full hidden md:flex shrink-0 z-20 transition-[width] duration-300 ease-out ${
        collapsed ? 'w-20' : 'w-72 lg:w-[276px]'
      }`}
      style={{
        background:
          'linear-gradient(180deg, #ffffff 0%, #ffffff 55%, #faf8f5 100%)',
        borderRight: '1px solid rgba(229, 225, 220, 0.8)',
      }}
    >
      {/* Header / Brand */}
      <div
        className={`transition-all duration-300 ${
          collapsed ? 'p-4' : 'px-6 pt-6 pb-5'
        }`}
      >
        <div className="flex items-center gap-3">
          <img
            src="/images/landing/aidfone-logo-horizontal.png"
            alt="AidFone"
            className={`transition-all duration-300 ${
              collapsed ? 'h-8 w-8 object-contain object-left' : 'h-8 w-auto'
            }`}
          />
        </div>
      </div>

      {/* Thin accent divider under logo */}
      <div
        className={`${collapsed ? 'mx-3' : 'mx-6'} h-px bg-gradient-to-r from-transparent via-brand-border to-transparent`}
      />

      {/* Navigation */}
      <nav
        className={`flex flex-col flex-1 overflow-y-auto overflow-x-hidden ${
          collapsed ? 'px-2 pt-4' : 'px-3 pt-6'
        }`}
        style={{ scrollbarGutter: 'stable' }}
      >
        {/* Primary section */}
        <div className="flex flex-col gap-0.5">
          {!collapsed && (
            <p className="px-3 mb-2 text-[10px] font-semibold text-brand-text-muted/70 tracking-[0.16em] uppercase">
              {t('sidebarUi.navigation')}
            </p>
          )}
          {mainMenuItems.map((item) => (
            <NavLink
              key={item.id}
              item={item}
              isActive={location.pathname === item.path}
              collapsed={collapsed}
              onClick={handleItemClick}
            />
          ))}
        </div>

        {/* Divider between sections (only when expanded) */}
        {!collapsed && (
          <div className="mx-3 my-5 h-px bg-brand-border/60" />
        )}

        {/* Settings section */}
        <div className={`flex flex-col gap-0.5 ${collapsed ? 'mt-4' : ''}`}>
          {!collapsed && (
            <p className="px-3 mb-2 text-[10px] font-semibold text-brand-text-muted/70 tracking-[0.16em] uppercase">
              {t('sidebarUi.account')}
            </p>
          )}
          {settingsMenuItems.map((item) => (
            <NavLink
              key={item.id}
              item={item}
              isActive={location.pathname === item.path}
              collapsed={collapsed}
              onClick={handleItemClick}
            />
          ))}
        </div>
      </nav>

      {/* Language toggle — floats centered, no divider above */}
      <div className={`flex ${collapsed ? 'justify-center px-2 py-3' : 'px-5 py-3'}`}>
        <LanguageToggle />
      </div>

      {/* User profile + sign out */}
      <div className={`${collapsed ? 'p-3' : 'px-5 pb-5'}`}>
        <div
          className={`relative rounded-2xl overflow-hidden ${
            collapsed ? '' : 'bg-brand-bg-warm/60 border border-brand-border/60'
          }`}
        >
          {/* User identity row */}
          <div
            className={`flex items-center gap-3 ${
              collapsed ? 'justify-center p-2' : 'p-3'
            }`}
          >
            <div
              className={`relative rounded-full flex items-center justify-center shrink-0 font-display font-bold text-brand-primary overflow-hidden ${
                collapsed ? 'w-10 h-10 text-base' : 'w-10 h-10 text-base'
              }`}
              style={{
                background:
                  'linear-gradient(135deg, rgba(30, 95, 138, 0.12) 0%, rgba(224, 123, 76, 0.10) 100%)',
              }}
              title={collapsed ? currentUser.name : undefined}
            >
              {currentUser.avatar ? (
                <img
                  src={currentUser.avatar}
                  alt={currentUser.name}
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                <span>{firstInitial}</span>
              )}
              {/* Online indicator dot */}
              <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-brand-success border-[1.5px] border-white" />
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-semibold text-brand-text truncate leading-tight">
                  {currentUser.name}
                </p>
                <p className="text-[11px] text-brand-text-muted truncate mt-0.5">
                  {t('sidebarUi.caregiverRole')}
                </p>
              </div>
            )}
          </div>

          {/* Sign out button — only when expanded, separate affordance */}
          {!collapsed && onLogout && (
            <button
              onClick={onLogout}
              className="w-full flex items-center justify-center gap-1.5 py-2 border-t border-brand-border/60 text-[12px] font-semibold text-brand-text-muted hover:text-brand-primary hover:bg-brand-primary/5 transition-colors"
            >
              <span className="material-symbols-outlined text-[16px]">logout</span>
              {t('sidebarUi.signOut')}
            </button>
          )}

          {/* Collapsed-mode: whole card is the sign-out target */}
          {collapsed && onLogout && (
            <button
              onClick={onLogout}
              aria-label={`${currentUser.name} — ${t('sidebarUi.signOut')}`}
              className="absolute inset-0 w-full h-full rounded-2xl focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
            />
          )}
        </div>
      </div>

      {/* Collapse toggle — pinned to the right edge as a floating pill.
          Stays 24px visually; before:-inset-2.5 gives it a ~44px invisible hit
          area so it meets the touch-target minimum without redesigning the
          pill that straddles the sidebar edge. */}
      {onToggleCollapse && (
        <button
          onClick={onToggleCollapse}
          aria-label={collapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar')}
          className="absolute top-24 -right-3 w-6 h-6 rounded-full bg-white border border-brand-border shadow-md flex items-center justify-center text-brand-text-muted hover:text-brand-primary hover:border-brand-primary/40 transition-all z-10 before:absolute before:-inset-2.5 before:content-['']"
        >
          <span
            className={`material-symbols-outlined text-[16px] transition-transform duration-300 ${
              collapsed ? 'rotate-180' : ''
            }`}
          >
            chevron_left
          </span>
        </button>
      )}
    </aside>
  );
};

export default Sidebar;
